import axios from "axios";

import { LayoutContainerModel } from "../../models/containers/layout/LayoutContainerModel";
import { LayoutsRepository } from "../../repositories/layout/layouts.repository";
import { layoutsCacheStore } from "../../db/runtime.storage";


export class LayoutsService {
    private readonly layoutsRepo = new LayoutsRepository();
    private readonly store = layoutsCacheStore; // referência ao cache global

    private buildListKey(
        pageNumber: number,
        pageSize: number,
        position?: number,
        name?: string,
        tag?: string,
        pageId?: number,
        partial = false,
        descending = false
    ): string {
        return JSON.stringify([
            "list",
            pageNumber,
            pageSize,
            position ?? null,
            name ?? null,
            tag ?? null,
            pageId ?? null,
            partial,
            descending
        ]);
    }

    private buildGetKey(
        position?: number,
        name?: string,
        tag?: string,
        pageId?: number,
        partial = false
    ): string {
        return JSON.stringify([
            "get",
            position ?? null,
            name ?? null,
            tag ?? null,
            pageId ?? null,
            partial
        ]);
    }

    private clone<T>(value: T): T {
        if (typeof structuredClone === "function") {
            return structuredClone(value);
        }
        return JSON.parse(JSON.stringify(value)) as T;
    }

    public async handleListLayoutContainersWithRange(
        pageNumber: number,
        pageSize: number,
        position?: number,
        name?: string,
        tag?: string,
        pageId?: number,
        partial = false,
        descending = false
    ): Promise<LayoutContainerModel[] | undefined> {
        const key = this.buildListKey(pageNumber, pageSize, position, name, tag, pageId, partial, descending);

        // 1. Cache hit
        const cached = this.store.listCache.get(key);
        if (cached !== undefined) {
            console.info("[LayoutsService] list cache hit:", { key });
            return this.clone(cached);
        }

        // 2. Requisição em andamento (dedup)
        const inFlight = this.store.listPending.get(key);
        if (inFlight) {
            console.info("[LayoutsService] list duplicated request recycled:", { key });
            return inFlight.then((data) => (data ? this.clone(data) : undefined));
        }

        // 3. Nova requisição
        const request = this.layoutsRepo
            .listLayoutContainersWithRange(pageNumber, pageSize, position, name, tag, pageId, partial, descending)
            .then((data: any) => {
                const result = (data ?? undefined) as LayoutContainerModel[] | undefined;
                if (result !== undefined) {
                    const snapshot = this.clone(result);
                    this.store.setListCache(key, snapshot);  // usa método que controla limite
                    return this.clone(snapshot);
                }
                return undefined;
            })
            .catch((error: unknown) => {
                if (axios.isAxiosError(error)) {
                    if (error.response) {
                        console.error(error.response.data || "Erro ao listar containers.");
                    } else if (error.request) {
                        console.error("Sem resposta do servidor");
                    } else {
                        console.error("Erro interno ao fazer a requisição.");
                    }
                }
                return undefined;
            })
            .finally(() => {
                this.store.listPending.delete(key);
            });

        this.store.listPending.set(key, request);
        return request;
    }

    public async handleGetLayoutContainer(
        position?: number,
        name?: string,
        tag?: string,
        pageId?: number,
        partial = false
    ): Promise<LayoutContainerModel | undefined> {
        const key = this.buildGetKey(position, name, tag, pageId, partial);

        const cached = this.store.getCache.get(key);
        if (cached !== undefined) {
            console.info("[LayoutsService] get cache hit:", { key, partial });
            return this.clone(cached);
        }

        const inFlight = this.store.getPending.get(key);
        if (inFlight) {
            console.info("[LayoutsService] get duplicated request recycled:", { key, partial });
            return inFlight.then((data) => (data ? this.clone(data) : undefined));
        }

        const request = this.layoutsRepo
            .getLayoutContainer(position, name, tag, pageId, partial)
            .then((data: any) => {
                const result = (data ?? undefined) as LayoutContainerModel | undefined;
                if (result !== undefined) {
                    const snapshot = this.clone(result);
                    this.store.getCache.set(key, snapshot);
                    return this.clone(snapshot);
                }
                return undefined;
            })
            .catch((error: unknown) => {
                if (axios.isAxiosError(error)) {
                    if (error.response) {
                        console.error(error.response.data || "Erro ao obter container.");
                    } else if (error.request) {
                        console.error("Sem resposta do servidor");
                    } else {
                        console.error("Erro interno ao fazer a requisição.");
                    }
                }
                return undefined;
            })
            .finally(() => {
                this.store.getPending.delete(key);
            });

        this.store.getPending.set(key, request);
        return request;
    }

    public async handleCountLayoutContainersWithRange(
        pageNumber: number,
        pageSize: number,
        position?: number,
        name?: string,
        tag?: string,
        pageId?: number
    ): Promise<number | undefined> {
        try {
            const data: any = await this.layoutsRepo.countLayoutContainersWithRange(
                pageNumber,
                pageSize,
                position,
                name,
                tag,
                pageId
            );
            return data ?? undefined;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                if (error.response) {
                    console.error(error.response.data || "Erro ao contar containers.");
                } else if (error.request) {
                    console.error("Sem resposta do servidor");
                } else {
                    console.error("Erro interno ao fazer a requisição.");
                }
            }
        }
    }
}